import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MessageCircle, Share2, Copy } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import api from "../lib/api";
import { waLink } from "../lib/clinic";
import SafeImage from "../components/site/SafeImage";
import ReadingProgress from "../components/site/ReadingProgress";
import AnswerSummary from "../components/site/AnswerSummary";
import ReviewedBadge from "../components/site/ReviewedBadge";
import TableOfContents from "../components/site/TableOfContents";
import RelatedTreatments from "../components/site/RelatedTreatments";
import RelatedArticles from "../components/site/RelatedArticles";
import AuthorCard from "../components/site/AuthorCard";
import MedicalDisclaimer from "../components/site/MedicalDisclaimer";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { AUTHOR_REF, PUBLISHER_REF, SITE, REF, SPEAKABLE, breadcrumb, graph } from "../lib/schema";

const Row = ({ id, heading, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div id={id} className="scroll-mt-24">
      <h2 className="font-serif text-3xl">{heading}</h2>
      <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3 mb-5" />
      <ul className="space-y-3 prose-elegant">
        {items.map((s, i) => (
          <li key={i} className="flex gap-3">
            <span className="text-[hsl(var(--accent))] mt-1">◆</span>
            <span>{s}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Block = ({ id, heading, body }) => {
  if (!body) return null;
  return (
    <div id={id} className="scroll-mt-24">
      <h2 className="font-serif text-3xl">{heading}</h2>
      <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3 mb-5" />
      <p className="prose-elegant whitespace-pre-line">{body}</p>
    </div>
  );
};

const buildToc = (item) => {
  const list = [
    { id: "overview", label: "Overview", show: !!item.overview },
    { id: "symptoms", label: "Symptoms", show: item.symptoms?.length },
    { id: "causes", label: "Causes", show: item.causes?.length },
    { id: "risk-factors", label: "Risk factors", show: item.risk_factors?.length },
    { id: "diagnosis", label: "Diagnosis", show: !!item.diagnosis && item.diagnosis !== "N/A" },
    { id: "treatment", label: "Treatment", show: !!item.treatment && item.treatment !== "N/A" },
    { id: "when-to-see", label: "When to see a doctor", show: !!item.when_to_see && item.when_to_see !== "N/A" },
    { id: "prevention", label: "Prevention", show: !!item.prevention && item.prevention !== "N/A" },
    { id: "faqs", label: "FAQs", show: item.faqs?.length },
    { id: "references", label: "References", show: item.references?.length },
  ];
  return list.filter((i) => i.show).map(({ id, label }) => ({ id, label }));
};

const HubArticle = () => {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);

  usePageMeta({
    title: item ? `${item.title} | Dr. Shantanu Gupta` : "Article | Dr. Shantanu Gupta",
    path: `/content-hub/${slug}`,
    description: item?.answer_summary || item?.excerpt,
  });

  const medicalSchema = useMemo(() => {
    if (!item) return null;
    const url = `${SITE}/content-hub/${slug}`;
    const nodes = [
      {
        "@type": "MedicalWebPage",
        "@id": `${url}#page`,
        name: item.title,
        description: item.answer_summary || item.excerpt,
        image: item.cover_image,
        url,
        speakable: SPEAKABLE,
        lastReviewed: item.medically_reviewed || "2026-02-15",
        dateModified: item.medically_reviewed || "2026-02-15",
        reviewedBy: AUTHOR_REF,
        author: AUTHOR_REF,
        publisher: PUBLISHER_REF,
        inLanguage: "en-IN",
        specialty: "https://schema.org/Ophthalmic",
        mainEntity: { "@id": `${url}#condition` },
      },
      {
        "@type": "MedicalCondition",
        "@id": `${url}#condition`,
        name: item.title,
        description: item.overview || item.excerpt,
        ...(item.symptoms?.length
          ? { signOrSymptom: item.symptoms.map((s) => ({ "@type": "MedicalSymptom", name: s })) }
          : {}),
        ...(item.causes?.length
          ? { cause: item.causes.map((c) => ({ "@type": "MedicalCause", name: c })) }
          : {}),
        ...(item.risk_factors?.length
          ? { riskFactor: item.risk_factors.map((r) => ({ "@type": "MedicalRiskFactor", name: r })) }
          : {}),
        ...(item.treatment && item.treatment !== "N/A"
          ? { possibleTreatment: { "@type": "MedicalTherapy", name: item.treatment.split("\n")[0].slice(0, 120) } }
          : {}),
        ...(item.related_expertise?.length
          ? {
              relevantSpecialty: {
                "@type": "MedicalSpecialty",
                name: "Ophthalmology",
              },
              associatedAnatomy: { "@type": "AnatomicalStructure", name: "Eye" },
              recognizingAuthority: { "@id": REF.physician },
            }
          : {}),
      },
    ];
    if (item.faqs?.length) {
      nodes.push({
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        mainEntity: item.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }
    nodes.push(
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Content Hub", path: "/content-hub" },
        { name: item.title, path: `/content-hub/${slug}` },
      ])
    );
    return graph(nodes);
  }, [item, slug]);
  usePageJsonLd(medicalSchema);

  useEffect(() => {
    setItem(null);
    setNotFound(false);
    api.get(`/hub/${slug}`).then((r) => setItem(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: item.title, url }); } catch { /* ignore */ }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    }
  };
  const copyLink = async () => { await navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); };

  if (notFound) return <div className="container-page py-32 text-center">Article not found. <Link to="/content-hub" className="text-[hsl(var(--primary))]">Back to hub</Link></div>;
  if (!item) return <div className="container-page py-32 text-center text-muted-foreground">Loading…</div>;

  const toc = buildToc(item);

  return (
    <article data-testid="hub-article-page">
      <ReadingProgress />
      <section className="pt-14 container-page">
        <Link to="/content-hub" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))]" data-testid="back-to-hub">
          <ArrowLeft size={14} /> Content Hub
        </Link>
      </section>

      <section className="pt-6 pb-10 container-page max-w-5xl">
        <div className="overline mb-4">{item.category}</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance">{item.title}</h1>
        <ReviewedBadge reviewed={item.medically_reviewed} readingTime={item.reading_time} />
      </section>

      {item.cover_image && (
        <section className="container-page">
          <SafeImage src={item.cover_image} alt={item.title} label={item.title} className="w-full max-h-[520px] rounded-2xl border border-border" style={{ aspectRatio: "16/9" }} />
        </section>
      )}

      <section className="py-14 container-page">
        <div className="grid gap-10 lg:gap-14 lg:grid-cols-[220px_minmax(0,1fr)_260px]">
          <div className="hidden lg:block">
            <TableOfContents items={toc} />
          </div>

          <div className="max-w-[760px] mx-auto lg:mx-0 w-full space-y-12">
            <AnswerSummary text={item.answer_summary} />
            <Block id="overview" heading="Overview" body={item.overview} />
            <Row id="symptoms" heading="Symptoms" items={item.symptoms} />
            <Row id="causes" heading="Causes" items={item.causes} />
            <Row id="risk-factors" heading="Risk factors" items={item.risk_factors} />
            <Block id="diagnosis" heading="Diagnosis" body={item.diagnosis && item.diagnosis !== "N/A" ? item.diagnosis : ""} />
            <Block id="treatment" heading="Treatment options" body={item.treatment && item.treatment !== "N/A" ? item.treatment : ""} />
            <Block id="when-to-see" heading="When to see a doctor" body={item.when_to_see && item.when_to_see !== "N/A" ? item.when_to_see : ""} />
            <Block id="prevention" heading="Prevention" body={item.prevention && item.prevention !== "N/A" ? item.prevention : ""} />

            {item.faqs?.length > 0 && (
              <div id="faqs" className="scroll-mt-24">
                <h2 className="font-serif text-3xl">Frequently Asked</h2>
                <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3 mb-5" />
                <Accordion type="single" collapsible className="border border-border rounded-xl overflow-hidden bg-card">
                  {item.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`h-${i}`} className="border-b last:border-b-0" data-testid={`hub-faq-${i}`}>
                      <AccordionTrigger className="text-left font-medium px-5 py-4 hover:bg-[hsl(var(--secondary))]/50">{f.q}</AccordionTrigger>
                      <AccordionContent className="px-5 pb-5 text-[hsl(var(--foreground))]/80 whitespace-pre-line leading-relaxed">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}

            {item.references?.length > 0 && (
              <div id="references" className="scroll-mt-24">
                <h2 className="font-serif text-3xl">References</h2>
                <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3 mb-5" />
                <ul className="prose-elegant list-disc pl-6 space-y-2">
                  {item.references.map((r, i) => (<li key={i}>{r}</li>))}
                </ul>
              </div>
            )}

            <RelatedTreatments slugs={item.related_expertise} />
            <MedicalDisclaimer />

            <div className="border border-border rounded-2xl p-6 bg-[hsl(var(--secondary))]/40">
              <a href={waLink(`Hi Dr. Shantanu, I read your article on ${item.title} and would like to book an appointment.`)} target="_blank" rel="nofollow noopener noreferrer" className="btn-primary" data-testid="hub-book-btn">
                <MessageCircle size={16} /> Book Consultation
              </a>
            </div>
          </div>

          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-6">
              <div className="rounded-2xl border border-border p-5 bg-[hsl(var(--secondary))]/40" data-testid="side-author-card">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-3">Reviewed by</div>
                <div className="font-serif text-lg leading-snug">Dr. Shantanu Kumar Gupta</div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mt-1">
                  MBBS · MS · DNB · FICO
                </div>
                <p className="mt-3 text-sm text-[hsl(var(--foreground))]/75 leading-relaxed">
                  Cataract, cornea & medical retina specialist at Shantanu Netralaya, Varanasi.
                </p>
                <Link to="/about" className="mt-4 inline-block text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
                  More about Dr. Shantanu →
                </Link>
              </div>

              <div className="rounded-2xl border border-border p-5">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-3">Share</div>
                <div className="flex gap-2">
                  <button onClick={share} className="btn-ghost !py-2 !px-3 text-xs flex-1" data-testid="hub-share"><Share2 size={13} /> Share</button>
                  <button onClick={copyLink} className="btn-ghost !py-2 !px-3 text-xs flex-1"><Copy size={13} /> Copy</button>
                </div>
              </div>

              <a href={waLink(`Hi Dr. Shantanu, I read your article: ${item.title}`)} target="_blank" rel="nofollow noopener noreferrer" className="block rounded-2xl border border-[hsl(var(--accent))]/40 p-5 bg-[hsl(var(--accent))]/5 hover:bg-[hsl(var(--accent))]/10 transition-colors" data-testid="hub-side-book">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-2">Book a consult</div>
                <div className="font-serif text-lg leading-snug">Talk to Dr. Shantanu on WhatsApp</div>
                <div className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[hsl(var(--accent))]"><MessageCircle size={13} /> Start chat</div>
              </a>
            </div>
          </aside>
        </div>
      </section>

      <section className="container-page lg:hidden">
        <AuthorCard />
      </section>

      <RelatedArticles
        kind="hub"
        currentSlug={item.slug}
        category={item.category}
        explicitSlugs={item.related_articles || []}
      />
    </article>
  );
};

export default HubArticle;
